import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TASK_PARTICIPATION_STATE_TOKEN } from './task-participation.token';
import { TaskParticipationService } from '../../services/task-participation.service';
import {
  AddTaskParticipationAction,
  GeneratePaymentReportAction,
  GenerateUserPaymentReportAction,
  GetUserTasksParticipationsAction,
  UpdateTaskParticipationAction,
} from './task-participation.actions';
import { takeWhile, tap } from 'rxjs';
import { TaskParticipationDTO } from '../../dtos/task-participation.dto';
import { AuthService } from 'src/app/platform/services/auth.service';
import { FilesService } from 'src/app/platform/services/files.service';

export interface ITaskParticipationState {
  userTaskParticipations?: TaskParticipationDTO[];
}

@State({
  name: TASK_PARTICIPATION_STATE_TOKEN,
  defaults: TaskParticipationState.defaultState,
})
@Injectable()
export class TaskParticipationState {
  static defaultState: ITaskParticipationState = {
    userTaskParticipations: [],
  };

  constructor(
    private readonly taskParticipationService: TaskParticipationService,
    private readonly authService: AuthService,
    private readonly filesService: FilesService,
  ) {}

  @Selector()
  static getUserTaskParticipations(state: ITaskParticipationState) {
    return state.userTaskParticipations;
  }

  @Action(GetUserTasksParticipationsAction)
  getUserTaskParticipation(
    { patchState }: StateContext<ITaskParticipationState>,
    { organizationId, studyId, username, filter }: GetUserTasksParticipationsAction,
  ) {
    return this.taskParticipationService.getUserTaskParticipation(organizationId, studyId, username, filter).pipe(
      tap((userTaskParticipations) => {
        patchState({
          userTaskParticipations,
        });
      }),
    );
  }

  @Action(AddTaskParticipationAction)
  addTaskParticipation(
    { getState, patchState }: StateContext<ITaskParticipationState>,
    { organizationId, studyId, username, payload }: AddTaskParticipationAction,
  ) {
    return this.taskParticipationService.addTaskParticipation(organizationId, studyId, username, payload).pipe(
      tap((newParticipation) => {
        const state = getState();
        patchState({
          userTaskParticipations: [...(state.userTaskParticipations || []), newParticipation],
        });
      }),
    );
  }

  @Action(UpdateTaskParticipationAction)
  updateTaskParticipation(
    { getState, patchState }: StateContext<ITaskParticipationState>,
    { organizationId, studyId, username, payload }: UpdateTaskParticipationAction,
  ) {
    return this.taskParticipationService.updateTaskParticipation(organizationId, studyId, username, payload).pipe(
      tap((updatedParticipation) => {
        const state = getState();
        const updatedParticipations = state.userTaskParticipations.map((task) => {
          if (task.id === updatedParticipation.id) {
            return {
              ...task,
              type: updatedParticipation.type,
              description: updatedParticipation.description,
              date: updatedParticipation.date,
              timeSpent: updatedParticipation.timeSpent,
            };
          }
          return task;
        });
        patchState({
          userTaskParticipations: updatedParticipations,
        });
      }),
    );
  }

  @Action(GenerateUserPaymentReportAction)
  generateUserPaymentReport(
    _: StateContext<ITaskParticipationState>,
    { organizationId, studyId, username, filter, config }: GenerateUserPaymentReportAction,
  ) {
    return this.taskParticipationService
      .generateUserPaymentReport(organizationId, studyId, username, filter, config)
      .pipe(
        takeWhile(() => this.authService.userIsLogged()),
        tap((response) => {
          this.filesService.download(response).subscribe();
        }),
      );
  }

  @Action(GeneratePaymentReportAction)
  generatePaymentReport(_: StateContext<ITaskParticipationState>, { filter, config }: GeneratePaymentReportAction) {
    return this.taskParticipationService.generatePaymentReport(filter, config).pipe(
      takeWhile(() => this.authService.userIsLogged()),
      tap((response) => {
        this.filesService.download(response).subscribe();
      }),
    );
  }
}
